import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppProvider } from '@shopify/polaris';
import '@shopify/polaris/build/esm/styles.css';
import frTranslations from '@shopify/polaris/locales/fr.json';
import enTranslations from '@shopify/polaris/locales/en.json';
import deTranslations from '@shopify/polaris/locales/de.json';
import esTranslations from '@shopify/polaris/locales/es.json';
import ptBRTranslations from '@shopify/polaris/locales/pt-BR.json';
import jaTranslations from '@shopify/polaris/locales/ja.json';
import nlTranslations from '@shopify/polaris/locales/nl.json';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import type { Language } from './i18n/translations';
import App from './App';
import { initAuth } from './lib/auth';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
      refetchOnWindowFocus: false,
    },
  },
});

// Cast to any to handle Polaris locale type mismatches across versions
const polarisLocaleMap: Record<Language, any> = {
  fr: frTranslations,
  en: enTranslations,
  de: deTranslations,
  es: esTranslations,
  'pt-BR': ptBRTranslations,
  ja: jaTranslations,
  nl: nlTranslations,
};

function AppWithPolaris() {
  const { language } = useLanguage();
  const polarisTranslations = polarisLocaleMap[language];

  return (
    <AppProvider i18n={polarisTranslations}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </AppProvider>
  );
}

// Detect platform + bootstrap the WooCommerce session (from the SSO token)
// before first render so the first API calls are authenticated.
initAuth().finally(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AppWithPolaris />
        </LanguageProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
});
